import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, Trophy, XCircle, Pause } from "lucide-react";

const STAGE_LABEL = {
  lead: "Lead",
  qualification: "Qualification",
  demonstration: "Demo",
  decision_makers: "Decision makers",
  technical_evaluation: "Technical evaluation",
  budgetary_quotes: "Budgetary quote",
  under_budget_approval: "Under budget approval",
  budget_approval: "Budget approved",
  purchase_enquiry: "Purchase enquiry",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
  dropped: "Dropped",
};

const TERMINAL_TONE = {
  won:     { icon: Trophy,  bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  lost:    { icon: XCircle, bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200" },
  dropped: { icon: Pause,   bg: "bg-stone-100",  text: "text-stone-700",   border: "border-stone-200" },
};

/**
 * Hard-confirmation dialog for a pipeline stage change. Renviro's rule is
 * "forward-only" — once a deal moves past a stage it cannot revert. So we
 * make the rep read the warning and click again. The destructive button
 * is bottom-right (the gravity well of mobile thumbs) so accidental taps
 * are minimised.
 */
export default function StageMoveConfirmDialog({ open, onOpenChange, fromStage, toStage, onConfirm, busy }) {
  const term = TERMINAL_TONE[toStage];
  const TermIcon = term?.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="stage-confirm-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-2xl grid place-items-center bg-amber-50 text-amber-700 border border-amber-200">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <DialogTitle className="font-display text-xl">Move this deal forward?</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Stage transition preview */}
          <div className="flex items-center justify-center gap-3 py-2">
            <span className="px-3 py-1.5 rounded-2xl bg-muted text-muted-foreground text-xs font-bold uppercase tracking-wider" data-testid="confirm-from-stage">
              {STAGE_LABEL[fromStage] || fromStage}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span
              className={`px-3 py-1.5 rounded-2xl text-xs font-bold uppercase tracking-wider border ${term ? `${term.bg} ${term.text} ${term.border}` : "bg-primary-50 text-primary-700 border-primary-200"}`}
              data-testid="confirm-to-stage"
            >
              {TermIcon && <TermIcon className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />}
              {STAGE_LABEL[toStage] || toStage}
            </span>
          </div>

          {/* Discipline warning */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-sm font-semibold text-amber-900">This cannot be reverted.</p>
            <p className="text-xs text-amber-800 leading-snug mt-1">
              Once you move a deal forward, you can't pull it back to an earlier stage. Make sure the current stage is genuinely complete before continuing.
            </p>
            {term && (
              <p className="text-xs text-amber-800 leading-snug mt-2">
                This will mark the deal as <strong>{STAGE_LABEL[toStage]}</strong> and close it. Outcome bonuses and the timeline will be updated accordingly.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-2xl tap" onClick={() => onOpenChange(false)} disabled={busy} data-testid="confirm-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="rounded-2xl tap"
            data-testid="confirm-advance"
          >
            {busy ? "Moving…" : "Yes, move forward"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
